/**
 * ScenarioContext - State management for scenario engine
 * 
 * Provides scenario state across the cockpit, handling:
 * - Active scenario axis and state
 * - Scenario transitions with animations
 * - Value lookups for scenario-bound blocks
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import { ScenarioAxis, ScenarioState, ScenarioTrigger, ScenarioAssumption } from '../../../lib/cockpit/schemas'

export interface ScenarioContextValue {
  // Current scenario axis configuration
  axis: ScenarioAxis | null
  
  // Active state ID
  activeState: string | null
  
  // Set the active state
  setActiveState: (stateId: string) => void
  
  // Get the scenario value for a given key and state
  getScenarioValue: <T>(binding: Record<string, T> | undefined, fallback: T) => T
  
  // Check if a state is active
  isStateActive: (stateId: string) => boolean
  
  // Get state label
  getStateLabel: (stateId: string) => string | undefined
  
  // Triggers for the current axis
  triggers: ScenarioTrigger[]
  
  // Assumptions for the active state
  activeAssumptions: ScenarioAssumption[]
  
  // All assumptions
  allAssumptions: ScenarioAssumption[]
  
  // Animation state
  isTransitioning: boolean
}

const ScenarioContext = createContext<ScenarioContextValue | null>(null)

export interface ScenarioProviderProps {
  children: React.ReactNode
  axis: ScenarioAxis | null
  defaultState?: string
  personaDefaultState?: string
}

export function ScenarioProvider({
  children,
  axis,
  defaultState,
  personaDefaultState,
}: ScenarioProviderProps) {
  // Determine initial state: persona default > axis default > first state
  const initialState = useMemo(() => {
    if (!axis) return null
    
    // Check persona default
    if (personaDefaultState && axis.states.some((s: any) => s.state_id === personaDefaultState)) {
      return personaDefaultState
    }
    
    // Check explicit default
    if (defaultState && axis.states.some((s: any) => s.state_id === defaultState)) {
      return defaultState
    }
    
    // Check axis default
    const axisDefault = axis.states.find((s: any) => s.is_default)
    if (axisDefault) {
      return axisDefault.state_id
    }
    
    // Fall back to first state
    return axis.states[0]?.state_id ?? null
  }, [axis, defaultState, personaDefaultState])

  const [activeState, setActiveStateInternal] = useState<string | null>(initialState)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Handle state transitions with animation
  const setActiveState = useCallback((stateId: string) => {
    if (!axis || stateId === activeState) return
    
    // Validate state exists
    if (!axis.states.some((s: any) => s.state_id === stateId)) {
      console.warn(`Invalid scenario state: ${stateId}`)
      return
    }
    
    setIsTransitioning(true)
    
    // Small delay for transition animation
    setTimeout(() => {
      setActiveStateInternal(stateId)
      setTimeout(() => setIsTransitioning(false), 150)
    }, 50)
  }, [axis, activeState])

  // Get value from scenario binding
  const getScenarioValue = useCallback(<T,>(
    binding: Record<string, T> | undefined,
    fallback: T
  ): T => {
    if (!binding || !activeState) return fallback
    return binding[activeState] ?? fallback
  }, [activeState])

  // Check if state is active
  const isStateActive = useCallback((stateId: string) => {
    return activeState === stateId
  }, [activeState])

  // Get state label
  const getStateLabel = useCallback((stateId: string) => {
    if (!axis) return undefined
    return axis.states.find((s: any) => s.state_id === stateId)?.label
  }, [axis])

  // Get triggers
  const triggers = useMemo(() => {
    return axis?.triggers ?? []
  }, [axis])

  // Get active assumptions
  const activeAssumptions = useMemo(() => {
    if (!axis?.assumptions) return []
    return axis.assumptions.filter((a: any) => 
      !a.applies_to_state_id || a.applies_to_state_id === activeState
    )
  }, [axis, activeState])

  // Get all assumptions
  const allAssumptions = useMemo(() => {
    return axis?.assumptions ?? []
  }, [axis])

  const value = useMemo<ScenarioContextValue>(() => ({
    axis,
    activeState,
    setActiveState,
    getScenarioValue,
    isStateActive,
    getStateLabel,
    triggers,
    activeAssumptions,
    allAssumptions,
    isTransitioning,
  }), [
    axis,
    activeState,
    setActiveState,
    getScenarioValue,
    isStateActive,
    getStateLabel,
    triggers,
    activeAssumptions,
    allAssumptions,
    isTransitioning,
  ])

  return (
    <ScenarioContext.Provider value={value}>
      {children}
    </ScenarioContext.Provider>
  )
}

// Hook to use scenario context
export function useScenario(): ScenarioContextValue {
  const context = useContext(ScenarioContext)
  
  if (!context) {
    // Return a no-op context when not inside provider
    return {
      axis: null,
      activeState: null,
      setActiveState: () => {},
      getScenarioValue: (_, fallback) => fallback,
      isStateActive: () => false,
      getStateLabel: () => undefined,
      triggers: [],
      activeAssumptions: [],
      allAssumptions: [],
      isTransitioning: false,
    }
  }
  
  return context
}

// Hook to check if we're in a scenario context
export function useHasScenario(): boolean {
  const context = useContext(ScenarioContext)
  return context?.axis !== null
}

export default ScenarioContext



